import { useState } from "react";
import { X, Clock, MessageSquare, Calendar, Coins } from "lucide-react";
import styles from "./bid-modal.module.css";

interface BidModalProps {
  bountyId: string;
  bountyTitle: string;
  rewardUsdc: number;
  onClose: () => void;
  onSubmit: (data: BidFormData) => void;
  isSubmitting?: boolean;
  error?: string | null;
}

export interface BidFormData {
  proposal: string;
  estimatedDays: number;
  contactInfo: string;
  proposedReward: number;
}

const MAX_PROPOSAL_LENGTH = 2000;
const MIN_PROPOSAL_LENGTH = 20;
const MIN_REWARD = 1;
const MAX_REWARD = 10000000; // 1000万

export function BidModal({
  bountyId,
  bountyTitle,
  rewardUsdc,
  onClose,
  onSubmit,
  isSubmitting = false,
  error: externalError = null,
}: BidModalProps) {
  const [form, setForm] = useState<BidFormData>({
    proposal: "",
    estimatedDays: 7,
    contactInfo: "",
    proposedReward: rewardUsdc,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof BidFormData, string>>>({});

  const handleChange = (field: keyof BidFormData, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof BidFormData, string>> = {};

    if (form.proposal.length < MIN_PROPOSAL_LENGTH) {
      newErrors.proposal = `方案描述至少需要 ${MIN_PROPOSAL_LENGTH} 字`;
    } else if (form.proposal.length > MAX_PROPOSAL_LENGTH) {
      newErrors.proposal = `方案描述不能超过 ${MAX_PROPOSAL_LENGTH} 字`;
    }

    if (form.estimatedDays < 1) {
      newErrors.estimatedDays = "预计完成时间至少为 1 天";
    } else if (form.estimatedDays > 90) {
      newErrors.estimatedDays = "预计完成时间不能超过 90 天";
    }

    if (form.proposedReward < MIN_REWARD) {
      newErrors.proposedReward = "期望报酬必须大于 0";
    } else if (form.proposedReward > MAX_REWARD) {
      newErrors.proposedReward = "期望报酬不能超过 1000 万";
    }

    if (!form.contactInfo.trim()) {
      newErrors.contactInfo = "请填写联系方式";
    } else if (form.contactInfo.length > 200) {
      newErrors.contactInfo = "联系方式过长";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit(form);
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h3 className={styles.title}>
            <MessageSquare size={20} />
            投递方案
          </h3>
          <button className={styles.closeBtn} onClick={onClose} disabled={isSubmitting}>
            <X size={20} />
          </button>
        </div>

        {/* Task Info */}
        <div className={styles.taskInfo}>
          <div className={styles.taskTitle}>{bountyTitle}</div>
          <div className={styles.taskReward}>赏金: ¥{rewardUsdc.toLocaleString("zh-CN")}</div>
        </div>

        {/* Form */}
        <div className={styles.form}>
          {/* 方案描述 */}
          <div className={styles.field}>
            <label className={styles.label}>
              方案描述
              <span className={styles.required}>*</span>
            </label>
            <textarea
              className={`${styles.textarea} ${errors.proposal ? styles.error : ""}`}
              rows={5}
              placeholder="描述您的解决方案思路，包括技术选型、实现步骤等..."
              value={form.proposal}
              onChange={(e) => handleChange("proposal", e.target.value)}
              disabled={isSubmitting}
            />
            <div className={styles.fieldFooter}>
              {errors.proposal ? (
                <span className={styles.errorText}>{errors.proposal}</span>
              ) : (
                <span />
              )}
              <span
                className={`${styles.charCount} ${
                  form.proposal.length > MAX_PROPOSAL_LENGTH ? styles.overLimit : ""
                }`}
              >
                {form.proposal.length}/{MAX_PROPOSAL_LENGTH}
              </span>
            </div>
          </div>

          {/* 预计完成时间 */}
          <div className={styles.field}>
            <label className={styles.label}>
              <Clock size={14} />
              预计完成时间
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.daysInputWrapper}>
              <input
                type="number"
                className={`${styles.input} ${errors.estimatedDays ? styles.error : ""}`}
                min={1}
                max={90}
                value={form.estimatedDays}
                onChange={(e) => handleChange("estimatedDays", parseInt(e.target.value) || 1)}
                disabled={isSubmitting}
              />
              <span className={styles.daysUnit}>天</span>
            </div>
            {errors.estimatedDays && <span className={styles.errorText}>{errors.estimatedDays}</span>}
          </div>

          {/* 期望报酬 */}
          <div className={styles.field}>
            <label className={styles.label}>
              <Coins size={14} />
              期望报酬
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.daysInputWrapper}>
              <input
                type="number"
                className={`${styles.input} ${errors.proposedReward ? styles.error : ""}`}
                min={MIN_REWARD}
                max={MAX_REWARD}
                value={form.proposedReward}
                onChange={(e) => handleChange("proposedReward", parseInt(e.target.value) || 0)}
                disabled={isSubmitting}
              />
              <span className={styles.daysUnit}>¥</span>
            </div>
            {errors.proposedReward ? (
              <span className={styles.errorText}>{errors.proposedReward}</span>
            ) : (
              <span className={styles.hint}>原赏金: ¥{rewardUsdc.toLocaleString("zh-CN")}</span>
            )}
          </div>

          {/* 联系方式 */}
          <div className={styles.field}>
            <label className={styles.label}>
              <Calendar size={14} />
              联系方式
              <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              className={`${styles.input} ${errors.contactInfo ? styles.error : ""}`}
              placeholder="手机号/微信/邮箱，方便发布者联系您"
              value={form.contactInfo}
              onChange={(e) => handleChange("contactInfo", e.target.value)}
              disabled={isSubmitting}
            />
            {errors.contactInfo && <span className={styles.errorText}>{errors.contactInfo}</span>}
          </div>
        </div>

        {/* Error Message */}
        {externalError && (
          <div className={styles.errorBanner}>
            {externalError}
          </div>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={isSubmitting}>
            取消
          </button>
          <button className={styles.submitBtn} onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className={styles.spinner} />
                投递中...
              </>
            ) : (
              "确认投递"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
